import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Auth0ProviderWithNavigate } from "./auth/Auth0ProviderWithNavigate";
import { ProductErrorBoundary } from "./next/app/errors/ProductErrorBoundary";
import { ProductShell } from "./next/app/ProductShell";
import { productExternalServices } from "./config/externalServices";
import { SentryRouteObserver } from "./config/SentryRouteObserver";
import { lazyImportForRecovery } from "./lib/deployChunkRecovery";
import "./runtime";

const ReactQueryDevtools = lazy(lazyImportForRecovery(
  () => import("@tanstack/react-query-devtools")
    .then(({ ReactQueryDevtools: component }) => ({ default: component })),
  'ProductApp/@tanstack/react-query-devtools',
));

const queryClient = new QueryClient();

function productIsDev(): boolean {
  const envVar = import.meta.env.VITE_ISEE_ENV || "";
  const debugVar = import.meta.env.VITE_DEBUG_MODE || "";
  return envVar.trim() === "dev" || debugVar === "true";
}

export function ProductDocumentMetadata() {
  useEffect(() => {
    document.title = "Pinpoint Product";
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.append(robots);
    }
    robots.content = "noindex, nofollow";

    document.head.querySelector('link[rel="canonical"]')?.remove();
    document.head.querySelector('meta[name="description"]')?.remove();
    document.head.querySelector('meta[name="twitter:card"]')?.remove();
    document.head.querySelectorAll('meta[property^="og:"]').forEach((node) => node.remove());
  }, []);
  return null;
}

export function ProductRouteRoot(): ReactNode {
  useEffect(() => {
    document.documentElement.classList.add("product-document");
    return () => {
      document.documentElement.classList.remove("product-document");
    };
  }, []);
  const productTree = (
    <QueryClientProvider client={queryClient}>
      <ProductShell isDev={productIsDev()} />
      {import.meta.env.VITE_RQ_DEVTOOLS === "true" && (
        <Suspense fallback={null}><ReactQueryDevtools initialIsOpen={false} /></Suspense>
      )}
    </QueryClientProvider>
  );
  return (
    <ProductErrorBoundary>
      <ProductDocumentMetadata />
      <Auth0ProviderWithNavigate>
        {productExternalServices.googleClientId
          ? <GoogleOAuthProvider clientId={productExternalServices.googleClientId}>{productTree}</GoogleOAuthProvider>
          : productTree}
      </Auth0ProviderWithNavigate>
    </ProductErrorBoundary>
  );
}

export function ProductApp() {
  return (
    <BrowserRouter>
      <SentryRouteObserver />
      <ProductRouteRoot />
    </BrowserRouter>
  );
}

export default ProductApp;
